/**
 * Return the policy whose term is in force on `targetDate`.
 *
 * Unlike `getSegmentInForceOn` (which picks a segment WITHIN one policy), this picks across separate
 * policies that each represent a different term. A bundled account can carry the current auto term and
 * the upcoming renewal term as two distinct policies (e.g. AUTO-002 active + AUTO-003 future), so
 * umbrella validation on a future-effective home must compare against the auto policy covering the same
 * date, not just whichever auto policy happens to be first in the list.
 *
 * A policy covers `targetDate` when effectiveDate <= targetDate < endDate (an absent endDate is treated
 * as open-ended). With no `targetDate` we keep the prior behavior of returning the first policy. When no
 * policy covers the date we fall back to the latest policy that has already started, and if the date
 * precedes every term, to the first policy.
 */
export const getPolicyInForceOn = (policies, targetDate) => {
  if (!policies?.length) {
    return undefined;
  }
  if (!targetDate) {
    return policies[0];
  }
  const target = new Date(targetDate);
  const covering = policies.find((policy) => {
    if (!policy.effectiveDate) {
      return false;
    }
    const effective = new Date(policy.effectiveDate);
    const end = policy.endDate ? new Date(policy.endDate) : null;
    return effective <= target && (!end || target < end);
  });
  if (covering) {
    return covering;
  }
  const started = policies
    .filter((policy) => policy.effectiveDate && new Date(policy.effectiveDate) <= target)
    .sort((a, b) => new Date(a.effectiveDate) - new Date(b.effectiveDate));
  return started.length ? started[started.length - 1] : policies[0];
};
